import React, { useEffect, useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { SpaCardGrid } from "./SpaCard";

/**
 * PremiumCarousel — horizontally auto-scrolling premium spa carousel.
 *
 * Behavior:
 *   - When `items.length <= visibleCount` → renders a static grid (no scroll).
 *   - When `items.length >  visibleCount` → renders a horizontal carousel that
 *     auto-advances every `autoplayMs` milliseconds. Pauses on hover/focus and
 *     while the tab is hidden. Loops seamlessly back to the start.
 *
 * Uses native scroll (`scroll-behavior: smooth`) so keyboard/touch users still
 * get their expected UX — the timer just calls scrollTo() on the container.
 */
export default function PremiumCarousel({
  items,
  autoplayMs = 2000,
  visibleCount = 6,
  testId = "premium-carousel",
}) {
  const trackRef = useRef(null);
  const timerRef = useRef(null);
  const [paused, setPaused] = useState(false);

  const shouldAutoplay = items.length > visibleCount;

  // Advance one "page" (viewport width) per tick; if we're near the end,
  // snap back to the very start so the loop feels continuous.
  const advance = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const step = el.clientWidth;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 8;
    el.scrollTo({
      left: atEnd ? 0 : el.scrollLeft + step,
      behavior: "smooth",
    });
  }, []);

  const goPrev = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const step = el.clientWidth;
    const atStart = el.scrollLeft <= 8;
    el.scrollTo({
      left: atStart ? el.scrollWidth : el.scrollLeft - step,
      behavior: "smooth",
    });
  }, []);

  const goNext = useCallback(() => {
    advance();
  }, [advance]);

  // Autoplay loop — respects hover/focus pause + tab-visibility.
  useEffect(() => {
    if (!shouldAutoplay || paused) return undefined;
    const tick = () => {
      if (document.visibilityState === "visible") advance();
    };
    timerRef.current = setInterval(tick, autoplayMs);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [shouldAutoplay, paused, autoplayMs, advance]);

  // Non-carousel path — static grid, identical layout to the rest of the home page sections.
  if (!shouldAutoplay) {
    return (
      <div
        data-testid={`${testId}-grid`}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        {items.map((s) => (
          <SpaCardGrid key={s.id} spa={s} />
        ))}
      </div>
    );
  }

  return (
    <div
      className="relative"
      data-testid={testId}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      {/* Scroll track */}
      <div
        ref={trackRef}
        className="flex gap-6 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2 -mx-2 px-2"
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          WebkitOverflowScrolling: "touch",
        }}
        data-testid={`${testId}-track`}
      >
        <style>{`[data-testid="${testId}-track"]::-webkit-scrollbar{display:none}`}</style>
        {items.map((s) => (
          <div
            key={s.id}
            className="snap-start shrink-0 w-full sm:w-[calc((100%-1.5rem)/2)] lg:w-[calc((100%-3rem)/3)]"
          >
            <SpaCardGrid spa={s} />
          </div>
        ))}
      </div>

      {/* Prev / Next controls */}
      <button
        type="button"
        onClick={goPrev}
        aria-label="Previous premium spas"
        data-testid={`${testId}-prev`}
        className="absolute -left-2 sm:-left-4 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-white/95 border border-[#E7DDCF] shadow-[0_4px_12px_rgba(17,24,39,0.08)] flex items-center justify-center text-[#1F4E3D] hover:bg-white hover:shadow-[0_6px_16px_rgba(17,24,39,0.12)] transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A86A]"
      >
        <ChevronLeft size={18} />
      </button>
      <button
        type="button"
        onClick={goNext}
        aria-label="Next premium spas"
        data-testid={`${testId}-next`}
        className="absolute -right-2 sm:-right-4 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-white/95 border border-[#E7DDCF] shadow-[0_4px_12px_rgba(17,24,39,0.08)] flex items-center justify-center text-[#1F4E3D] hover:bg-white hover:shadow-[0_6px_16px_rgba(17,24,39,0.12)] transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A86A]"
      >
        <ChevronRight size={18} />
      </button>
    </div>
  );
}
